<script setup lang="ts">
import {Form, FormInstance} from "@/shared/components/ui/form2";
import {Button} from "@/shared/components/ui/button";
import {ref} from "vue";
import * as Yup from 'yup';
import SubmitButton from "@/shared/components/SubmitButton.vue";
import {faBadgeCheck, faCartShoppingFast, faRotateLeft} from "@fortawesome/pro-solid-svg-icons";
import {subYears} from 'date-fns';
import {GeoSearchResult} from "@/shared/composable/global.model.ts";
import {AddressInput} from "@/shared/graphql/types";
import AddressFormInput from "@/shared/components/address/AddressFormInput.vue";
import {FormRadioCardGroup} from "@/shared/components/ui/form2/element";
import {RadioCard} from "@/shared/components/ui/radio-group";
import {deepClone} from "@/shared/lib/polyfills.ts";

const formRef = ref()
const initialUser = {
  firstname: "Eli",
  lastname: "Willaert",
  age: 43,
  country: "DE",
  memory: "1",
  cadeau: "YES",
  bio: 'Eiusmod commodo minim nulla mollit nostrud ut. Enim est Lorem nulla et voluptate cillum anim labore ex. Velit consequat deserunt ex deserunt sit officia excepteur eu non officia tempor in. Occaecat aliquip ea officia ullamco pariatur cillum dolore laborum anim aliquip ipsum cupidatat et. Sint quis adipisicing in sit consectetur esse quis ipsum cillum. Adipisicing irure laborum voluptate nostrud.',
  birthdate: null,
  birthday2: new Date(),
  arrival: "20:15",
  friends: 0,
  customer: 1023,
  marketing_emails: true,
  company: {
    name: "Marcando"
  },
  address: {
    city: "Torhout",
    country: "BE",
    houseNumber: "6",
    street: "Zanbergstraat",
    zipCode: "8820"
  } as AddressInput
}
const user = ref(deepClone(initialUser))
const handleReset = () => {
  // Reset by creating a new deep clone of initial values
  user.value = deepClone(initialUser)
}
const handleManualSubmit = async () => {
  await (formRef.value as FormInstance).submit()
}
const handleManualValidate = async () => {
  const isValid = await (formRef.value as FormInstance).validate()
  console.log('Form is valid:', isValid)
}
const validationSchema = Yup.object().shape({
  firstname: Yup.string()
      .required('First name is required')
      .min(2, 'First name must be at least 2 characters')
      .max(50, 'First name must not exceed 50 characters'),
  birthdate: Yup.date().required("Birthday is required")
      .max(new Date(), "Date cannot be in the future")
      .max(subYears(new Date(), 18), "You must be at least 18 years old"),
  lastname: Yup.string()
      .required('Last name is required')
      .min(2, 'Last name must be at least 2 characters')
      .max(50, 'Last name must not exceed 50 characters'),
  bio: Yup.string()
      .min(2, 'Last name must be at least 2 characters'),
  age: Yup.number()
      .positive('Age must be a positive number')
      .integer('Age must be an integer')
      .min(18, 'Must be at least 18 years old')
      .max(120, 'Age must not exceed 120'),

  company: Yup.object().shape({
    name: Yup.string()
        .required('Company name is required')
        .min(2, 'Company name must be at least 2 characters')
        .max(100, 'Company name must not exceed 100 characters')
  }),
  address: Yup.object().shape({
    street: Yup.string()
        .required('Straat is verplicht')
  })
});

const showCompany = ref(true)

const geoAutoComplete = async (term: string): Promise<GeoSearchResult[]> => {
  try {
    const response = await fetch(`${import.meta.env.VITE_MCD_API}/geo/locations?q=${encodeURIComponent(term)}`);
    return await response.json() as GeoSearchResult[];
  } catch (error) {
    console.error('Error fetching geo locations:', error);
  }
}

interface Customer {
  id: number;
  name: string;
}

const customers: Customer[] = [
  {id: 1018, name: "Emma Johnson"},
  {id: 1019, name: "Liam Chen"},
  {id: 1020, name: "Sophia Rodriguez"},
  {id: 1021, name: "Noah Patel"},
  {id: 1022, name: "Olivia Kim"},
  {id: 1023, name: "Ethan Nguyen"},
  {id: 1024, name: "Ava Gupta"},
  {id: 1025, name: "Mason Thompson"},
  {id: 1026, name: "Isabella Martinez"},
  {id: 1027, name: "William Lee"},
  {id: 1028, name: "Mia Anderson"},
  {id: 1029, name: "James Wilson"},
  {id: 1030, name: "Charlotte Brown"},
  {id: 1031, name: "Benjamin Taylor"},
  {id: 1032, name: "Amelia Davis"},
  {id: 1033, name: "Lucas Garcia"},
  {id: 1034, name: "Harper Smith"},
  {id: 1035, name: "Alexander Wong"},
  {id: 1036, name: "Evelyn Murphy"},
  {id: 1037, name: "Daniel Jackson"}
];

const customer = ref<Customer>(customers.find(c => c.id === user.value.customer))
const searchCustomers = async (term: string, size: number = 10, page: number = 0): Promise<Customer[]> => {
  const lowercaseTerm = term.toLowerCase();

  const filteredCustomers = customers.filter(customer =>
      customer.name.toLowerCase().includes(lowercaseTerm)
  );

  const startIndex = page * size;
  const endIndex = startIndex + size;

  return Promise.resolve(filteredCustomers.slice(startIndex, endIndex));
};

</script>

<template>
  <div class="p-default">
    <Form
        ref="formRef"
        v-model="user"
        :debug="true"
        :validation="validationSchema"
        @submit="(user) => console.log('Form submitted:', user)">
      <h1 class="text-4xl font-bold text-gray-800 mb-4 leading-tight">Form tester</h1>

      <div class="w-full">
        <h1>Address</h1>

        <AddressFormInput
            v-model="user.address"
            v-validation="{path: 'address'}"
        />
      </div>

      <!--      <Grid class="w-1/4 gap-y-default" align="start">-->
      <!--        &lt;!&ndash;        <FormInput label="Firstname" v-model="user.firstname" v-validation  />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormInput label="Lastname" v-model="user.lastname" v-validation />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <Grid :columns="2">&ndash;&gt;-->
      <!--        &lt;!&ndash;          <FormDatePicker label="Birthdate" v-model="user.birthdate" v-validation />&ndash;&gt;-->
<!--                        <FormSelect label="Cadeau" v-model="user.cadeau" placeholder="Kies een verrassing">-->
<!--                          <SelectItem label="Ik wens een cadeautje voor mijn verjaardag" value="YES" />-->
<!--                          <SelectItem label="Ik wil niets ontvangen" value="NO" />-->
<!--                        </FormSelect>-->
      <!--        &lt;!&ndash;        </Grid>&ndash;&gt;-->

      <!--        <FormAutocomplete v-model="user.customer" placeholder="Zoeken..."-->
      <!--                          label="Klant"-->
      <!--                          :search="searchCustomers"-->
      <!--                          :clearable="true"-->
      <!--                          :input-value="customer"-->
      <!--                          :output-value='(c:Customer) => c ? c.id : null'-->
      <!--                          :display-value="(c: Customer) => c.name"-->
      <!--        >-->
      <!--          <template #item="customer: Customer">-->
      <!--            <div class="w-full flex items-center justify-between">-->
      <!--              <span>{{ customer.name }}</span>-->
      <!--              <small class="text-slate-500">{{ customer.id }}</small>-->
      <!--            </div>-->

      <!--          </template>-->
      <!--        </FormAutocomplete>-->

      <!--        &lt;!&ndash;        <FormTimeInput label="Aankomst" v-model="user.arrival" />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormInput label="Age" v-model="user.age" v-validation type="number" />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormTextarea label="Bio" v-model="user.bio" v-validation :rows="5" />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormCheckbox label="Ben je een bedrijf?" v-model="showCompany" />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormInput label="Company" v-if="showCompany" v-model="user.company.name" v-validation />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormCountrySelect label="Land" :countries="countries" v-model="user.country"  />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormRadioButtonGroup v-model="user.memory" :item-description="'inline'" name="otherMemory" label="Select other memory" description="Dolore ullamco ex dolor ea labore qui exercitation labore aliquip deserunt sint aliquip.">&ndash;&gt;-->
      <!--        &lt;!&ndash;          <RadioButton value="1" label="Small" description="4 GB RAM / 2 CPUS / 80 GB SSD Storage"/>&ndash;&gt;-->
      <!--        &lt;!&ndash;          <RadioButton value="2" label="Medium" description="8 GB RAM / 4 CPUS / 160 GB SSD Storage"/>&ndash;&gt;-->
      <!--        &lt;!&ndash;          <RadioButton value="3" label="Large" description="16 GB RAM / 8 CPUS / 320 GB SSD Storage"/>&ndash;&gt;-->
      <!--        &lt;!&ndash;        </FormRadioButtonGroup>&ndash;&gt;-->
                      <FormRadioCardGroup v-model="user.memory" label="Select memory" description="Dolore ullamco ex dolor ea labore qui exercitation labore aliquip deserunt sint aliquip." :cols="1">
                        <RadioCard value="1" label="Small" description="4 GB RAM / 2 CPUS / 80 GB SSD Storage"/>
                        <RadioCard value="2" label="Medium" description="8 GB RAM / 4 CPUS / 160 GB SSD Storage"/>
                        <RadioCard value="3" label="Large" description="16 GB RAM / 8 CPUS / 320 GB SSD Storage"/>
                        <RadioCard value="4" label="Extra Large" description="32 GB RAM / 8 CPUS / 320 GB SSD Storage"/>
                      </FormRadioCardGroup>
      <!--        &lt;!&ndash;        <FormSwitch&ndash;&gt;-->
      <!--        &lt;!&ndash;          v-model="user.marketing_emails"&ndash;&gt;-->
      <!--        &lt;!&ndash;          name="marketing_mails"&ndash;&gt;-->
      <!--        &lt;!&ndash;          label=Marketing e-mails&ndash;&gt;-->
      <!--        &lt;!&ndash;          description="U kunt e-mailadressen beheren in uw <a class='text-primary' href='/examples/forms'>e-mailinstellingen</a>."&ndash;&gt;-->
      <!--        &lt;!&ndash;        />&ndash;&gt;-->
      <!--        &lt;!&ndash;        <FormSlider :max="100" :min="0" v-model="user.friends" description="Aantal vrienden dat het evenement zal bijwonen" suffix="vrienden" />&ndash;&gt;-->
      <!--      </Grid>-->

      <div class="flex gap-default">
        <SubmitButton
            class="w-1/4 bg-black hover:bg-gray-600"
            :icon="faCartShoppingFast"
            :loading="false">
          Save
        </SubmitButton>

        <Button variant="secondary" :icon="faRotateLeft" @click="handleReset">Reset</Button>
        <Button variant="secondary" :icon="faBadgeCheck" @click="handleManualValidate">Manual validate</Button>

      </div>
    </Form>
  </div>

</template>
<style scoped>
/* You can add additional scoped styles here if needed */
</style>